import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EntityFormPage } from "@/components/entity-page/EntityFormPage";
import { FormSection } from "@/components/entity-page/FormSection";
import { ConfigField } from "@/components/entity-page/ConfigField";
import { DeleteConfirmPanel } from "@/components/entity-page/DeleteConfirmPanel";
import EditIntegrationForm from "@/components/EditIntegrationForm";
import { fetchClient, deleteIntegration } from "@/lib/api";
import type { SerializedIntegration } from "@/types";

export const Route = createFileRoute("/dashboard/clients/$id/integrations/$integrationId/")({
  component: IntegrationPage,
});

function IntegrationPage() {
  const { id, integrationId } = Route.useParams();
  return <IntegrationPageComponent businessId={id} integrationId={integrationId} />;
}

// Exported for testing
export function IntegrationPageComponent({
  businessId: id,
  integrationId,
}: {
  businessId: string;
  integrationId: string;
}) {
  const navigate = useNavigate();
  const [integration, setIntegration] = useState<SerializedIntegration | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const load = useCallback(() => {
    let active = true;
    fetchClient(id)
      .then((response) => {
        if (!active) return;
        const found = (response.integrations || []).find((i) => i.id === integrationId);
        if (!found) {
          setNotFound(true);
        } else {
          setIntegration(found);
        }
        setLoading(false);
      })
      .catch((error) => {
        if (!active) return;
        console.error("Error fetching client:", error);
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, integrationId]);

  useEffect(() => load(), [load]);

  const goToClient = () =>
    navigate({ to: "/dashboard/clients/$id", params: { id } });

  const handleDelete = async () => {
    if (!integration) return;
    try {
      const result = await deleteIntegration(integration.id);
      if (result.success) {
        toast.success("Integration deleted successfully");
        goToClient();
      } else {
        toast.error("Failed to delete integration", { description: result.error });
      }
    } catch (error) {
      console.error("Error deleting integration:", error);
      toast.error("An error occurred", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !integration) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Integration not found</div>
          </div>
        </div>
      </div>
    );
  }

  const isReactivator = integration.typeId === "reactivator";
  const isCalendly = integration.typeId === "calendly";
  const isGoogleCalendar = integration.typeId === "googleCalendar";

  return (
    <EntityFormPage
      title={integration.name}
      description={`Update ${integration.name}'s configuration.`}
      backHref={`/dashboard/clients/${id}`}
      headerActions={
        showDeleteConfirm ? undefined : mode === "view" ? (
          <>
            <Button type="button" variant="outline" onClick={() => setMode("edit")}>
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </>
        ) : (
          <>
            <Button type="button" variant="outline" onClick={() => setMode("view")}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </>
        )
      }
    >
      {showDeleteConfirm ? (
        <DeleteConfirmPanel
          title="Delete Integration?"
          description="This will permanently delete the integration and all associated data."
          confirmLabel="Delete Integration"
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
        />
      ) : mode === "view" ? (
        <div className="space-y-6">
          <FormSection title="Integration Details">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <ConfigField label="Name" value={integration.name} />
              <ConfigField label="Integration Type" value={integration.typeId} mono />
              <ConfigField label="Status" value={integration.isActive ? "Active" : "Inactive"} />
            </div>
          </FormSection>

          {isReactivator && (
            <FormSection title="GoHighLevel Configuration">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <ConfigField label="GoHighLevel Location ID" value={integration.ghlLocationId} mono />
                <ConfigField label="GoHighLevel Auth Token" value={integration.ghlAuthToken} secret />
                <ConfigField label="Webhook Token" value={integration.webhookToken} secret />
              </div>
            </FormSection>
          )}

          {isCalendly && (
            <FormSection title="Calendly Configuration">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <ConfigField label="Calendly API Token" value={integration.apiToken} secret />
                <ConfigField label="Default Event Type URI" value={integration.defaultEventType} mono />
                <ConfigField label="Default Event Host" value={integration.defaultEventHost} />
              </div>
            </FormSection>
          )}

          {isGoogleCalendar && (
            <FormSection
              title="Google Calendar Configuration"
              description="The client connects this from their own dashboard - there's no credential for staff to manage here."
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <ConfigField
                  label="Connection Status"
                  value={
                    integration.connectionStatus === "connected"
                      ? "Connected"
                      : integration.connectionStatus === "error"
                        ? "Error"
                        : "Not yet connected"
                  }
                />
                {integration.connectionStatus === "connected" && (
                  <ConfigField label="Connected Account" value={integration.connectedEmail} />
                )}
                {integration.connectionStatus === "error" && (
                  <ConfigField label="Last Error" value={integration.connectionError} />
                )}
              </div>
            </FormSection>
          )}
        </div>
      ) : (
        <EditIntegrationForm
          integration={integration}
          onSaved={() => {
            load();
            setMode("view");
          }}
        />
      )}
    </EntityFormPage>
  );
}
